import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { getPlural } from 'lib/string/pluralize'

/**
 * Returns an ICU-pluralized "count + saleUnit" string (e.g. "2 rooms", "1 villa")
 * for all known sale unit values, with a getPlural fallback for unknown units.
 *
 * Use the hook version (`useSaleUnitPlural`) in React components.
 * Use this standalone function in selectors/utils that already receive `intl`.
 */
export function formatSaleUnitCount(intl: IntlShape, saleUnit: string, count: number): string {
  const key = saleUnit.toLowerCase()
  const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase() && saleUnit[0] !== saleUnit[0].toLowerCase()

  let result: string | undefined

  if (key === 'room') {
    result = intl.formatMessage({
      id: 'nLcmGau2j6XBvHXOKwC6',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Pluralised sale unit label showing the number of rooms in the hotel offer pricing',
    }, { count })
  } else if (key === 'night') {
    result = intl.formatMessage({
      id: 'h3_iVk6wVBAnOg1R2eny',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Pluralised sale unit label showing the number of nights in the offer pricing',
    }, { count })
  } else if (key === 'villa') {
    result = intl.formatMessage({
      id: 'VWLEz2TZE_v27KGj7_p2',
      defaultMessage: '{count, plural, one {# villa} other {# villas}}',
      description: 'Pluralised sale unit label showing the number of villas in the offer pricing',
    }, { count })
  } else if (key === 'suite') {
    result = intl.formatMessage({
      id: 'iFxQNxrb3i1z963Dp1XJ',
      defaultMessage: '{count, plural, one {# suite} other {# suites}}',
      description: 'Pluralised sale unit label showing the number of suites in the offer pricing',
    }, { count })
  } else if (key === 'guest') {
    result = intl.formatMessage({
      id: 'WlE6FWgCVfrbvQPPIndu',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Pluralised sale unit label showing the number of guests in the offer pricing',
    }, { count })
  } else if (key === 'adult') {
    result = intl.formatMessage({
      id: '6SlT4c8Aa4PXEwaHG0N6',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Pluralised sale unit label showing the number of adults in the offer pricing',
    }, { count })
  } else if (key === 'traveller') {
    result = intl.formatMessage({
      id: '4gz2GNYpPhcRf2JMJtIZ',
      defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
      description: 'Sale unit count for travellers in the traveller details form',
    }, { count })
  } else if (key === 'person') {
    result = intl.formatMessage({
      id: '6-HH7f0_A5N4dtkBf-vX',
      defaultMessage: '{count, plural, one {# person} other {# people}}',
      description: 'Pluralised sale unit label showing the number of people in the offer pricing',
    }, { count })
  } else if (key === 'cabin') {
    result = intl.formatMessage({
      id: 'nwc7xwFhLB5fnrutCxLj',
      defaultMessage: '{count, plural, one {# cabin} other {# cabins}}',
      description: 'Pluralised sale unit label showing the number of cabins in the cruise pricing',
    }, { count })
  } else if (key === 'package') {
    result = intl.formatMessage({
      id: 'TR2vkZRKyP6-FXpBsPEv',
      defaultMessage: '{count, plural, one {# package} other {# packages}}',
      description: 'Pluralised sale unit label showing the number of packages in the offer pricing',
    }, { count })
  } else if (key === 'property') {
    result = intl.formatMessage({
      id: 'oD4BmJJv37z1mAnb_5df',
      defaultMessage: '{count, plural, one {# property} other {# properties}}',
      description: 'Sale unit count for properties for computed display labels',
    }, { count })
  }

  if (result) {
    if (startsWithCapital) {
      // Preserve capitalization of the unit word (e.g. "2 rooms" → "2 Rooms")
      const spaceIdx = result.indexOf(' ')
      if (spaceIdx !== -1 && spaceIdx + 1 < result.length) {
        return result.slice(0, spaceIdx + 1) + result[spaceIdx + 1].toUpperCase() + result.slice(spaceIdx + 2)
      }
    }
    return result
  }

  // i18n: fallback for unknown sale units — uses getPlural (English-only)
  return intl.formatMessage({
    id: 'FLCql9MZpVrinXe5R57E',
    defaultMessage: '{count, plural, one {# {saleUnit}} other {# {saleUnitPlural}}}',
    description: 'Fallback sale unit count for unrecognized sale units',
  }, { count, saleUnit, saleUnitPlural: getPlural(saleUnit) })
}

/**
 * Returns the plural noun for a known sale unit (e.g. "rooms", "villas", "people").
 * Falls back to getPlural for unknown units.
 *
 * Use this when you need just the word without a count, e.g. "Select your rooms".
 */
export function getSaleUnitPluralNoun(saleUnit: string): string {
  const known: Record<string, string> = {
    room: 'rooms',
    night: 'nights',
    villa: 'villas',
    suite: 'suites',
    guest: 'guests',
    adult: 'adults',
    traveller: 'travellers',
    person: 'people',
    cabin: 'cabins',
    package: 'packages',
    property: 'properties',
  }
  const result = known[saleUnit.toLowerCase()]
  if (result) {
    const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase() && saleUnit[0] !== saleUnit[0].toLowerCase()
    if (startsWithCapital) {
      return result.charAt(0).toUpperCase() + result.slice(1)
    }
    return result
  }
  return getPlural(saleUnit)
}

interface SaleUnitPluralFns {
  formatSaleUnitCount: (saleUnit: string, count: number) => string
  getSaleUnitPluralNoun: (saleUnit: string) => string
}

function useSaleUnitPlural(): SaleUnitPluralFns {
  const intl = useIntl()
  return useMemo(() => ({
    formatSaleUnitCount: (saleUnit: string, count: number) => formatSaleUnitCount(intl, saleUnit, count),
    getSaleUnitPluralNoun,
  }), [intl])
}

export default useSaleUnitPlural
